import { CloudDownloadError } from '@/modules/upload/cloud/CloudDownloadError'
import {
  bearerAuthorization,
  type DownloadTiming,
  downloadFromLink,
} from '@/modules/upload/cloud/downloadFromLink'
import type { CloudSource, PickedCloudFile } from '@/modules/upload/cloud/types'
import { PDF_MIME_TYPE } from '@/modules/upload/constants'
import {
  type GooglePicker,
  type GooglePickerDocument,
  type GooglePickerResponse,
  googleGlobals,
  loadedPicker,
} from './googleApis'
import { requestDriveToken, revokeDriveToken } from './googleSignIn'

export interface GoogleDriveKeys {
  clientId?: string
  apiKey?: string
  appId?: string
}

const DRIVE_FILES_URL = 'https://www.googleapis.com/drive/v3/files'
const MY_DRIVE_ROOT = 'root'
const WHOLE_BYTE_COUNT = /^\d+$/

export const hasEveryGoogleDriveKey = (
  keys: GoogleDriveKeys,
): keys is Required<GoogleDriveKeys> =>
  Boolean(keys.clientId && keys.apiKey && keys.appId)

const configuredKeys = (keys: GoogleDriveKeys) => {
  if (!hasEveryGoogleDriveKey(keys))
    throw new Error('Google Drive keys are not set')
  return keys
}

const driveFileUrl = (fileId: string, query: Record<string, string>) =>
  `${DRIVE_FILES_URL}/${encodeURIComponent(fileId)}?${new URLSearchParams(query)}`

// The Picker and the Drive API can both send a byte count as a number or as a string of digits.
const parseByteCount = (value: unknown) => {
  const digits = typeof value === 'number' ? String(value) : value
  if (typeof digits !== 'string' || !WHOLE_BYTE_COUNT.test(digits)) return null
  const byteCount = Number(digits)
  return Number.isSafeInteger(byteCount) ? byteCount : null
}

const readSizeField = async (metadata: Blob): Promise<unknown> => {
  try {
    return JSON.parse(await metadata.text()).size
  } catch {
    throw new CloudDownloadError('unknown-size')
  }
}

const fetchListedSize = async (
  fileId: string,
  token: string,
  timing?: DownloadTiming,
) => {
  const link = driveFileUrl(fileId, {
    fields: 'size',
    supportsAllDrives: 'true',
  })
  const metadata = await downloadFromLink(
    link,
    timing,
    bearerAuthorization(token),
  )
  const size = parseByteCount(await readSizeField(metadata))
  if (size === null) throw new CloudDownloadError('unknown-size')
  return size
}

// A file whose size could not be found is never downloaded: it fails with the reason it could not be sized.
const unsizedFile = (name: string, error: unknown): PickedCloudFile => ({
  name,
  size: 0,
  download: () => Promise.reject(error),
})

const toPickedFile = async (
  { id, name, sizeBytes }: GooglePickerDocument,
  token: string,
  timing?: DownloadTiming,
): Promise<PickedCloudFile> => {
  const link = driveFileUrl(id, { alt: 'media', supportsAllDrives: 'true' })
  const download = () =>
    downloadFromLink(link, timing, bearerAuthorization(token))
  try {
    const size =
      parseByteCount(sizeBytes) ?? (await fetchListedSize(id, token, timing))
    return { name, size, download }
  } catch (error) {
    return unsizedFile(name, error)
  }
}

// Google Docs and Sheets are not PDFs, so no view offers them. Folders show so the customer can open them, but cannot be picked.
const pdfAndFolderView = (picker: GooglePicker) =>
  new picker.DocsView(picker.ViewId.DOCS)
    .setMimeTypes(PDF_MIME_TYPE)
    .setIncludeFolders(true)
    .setSelectFolderEnabled(false)

const myDriveView = (picker: GooglePicker) =>
  pdfAndFolderView(picker).setParent(MY_DRIVE_ROOT)

const sharedWithMeView = (picker: GooglePicker) =>
  new picker.DocsView(picker.ViewId.DOCS)
    .setMimeTypes(PDF_MIME_TYPE)
    .setOwnedByMe(false)

const sharedDrivesView = (picker: GooglePicker) =>
  pdfAndFolderView(picker).setEnableDrives(true)

// The Picker also calls back when it has loaded; only a pick, a cancel or an error ends it.
const settlePick = (
  picker: GooglePicker,
  response: GooglePickerResponse,
  resolve: (documents: GooglePickerDocument[]) => void,
  reject: (error: Error) => void,
) => {
  if (response.action === picker.Action.PICKED) resolve(response.docs ?? [])
  if (response.action === picker.Action.CANCEL) resolve([])
  if (response.action === picker.Action.ERROR) {
    reject(new Error('Google Picker failed'))
  }
}

const openPicker = (token: string, keys: Required<GoogleDriveKeys>) =>
  new Promise<GooglePickerDocument[]>((resolve, reject) => {
    const picker = loadedPicker()
    new picker.PickerBuilder()
      .addView(myDriveView(picker))
      .addView(sharedWithMeView(picker))
      .addView(sharedDrivesView(picker))
      .enableFeature(picker.Feature.MULTISELECT_ENABLED)
      .setOAuthToken(token)
      .setDeveloperKey(keys.apiKey)
      .setAppId(keys.appId)
      .setCallback((response) => settlePick(picker, response, resolve, reject))
      .build()
      .setVisible(true)
  })

const isLoaded = () =>
  Boolean(
    googleGlobals().google?.accounts?.oauth2 && googleGlobals().google?.picker,
  )

// The token lives only in this closure, never in storage, and is revoked when the import ends.
export const createGoogleDriveSource = (
  keys: GoogleDriveKeys,
  timing?: DownloadTiming,
): CloudSource => {
  let accessToken: string | null = null
  const pickWithToken = async (configured: Required<GoogleDriveKeys>) => {
    accessToken = await requestDriveToken(configured.clientId)
    if (!accessToken) return []
    const token = accessToken
    const documents = await openPicker(token, configured)
    return Promise.all(
      documents.map((document) => toPickedFile(document, token, timing)),
    )
  }
  return {
    label: 'Google Drive',
    blockedLinkAdvice:
      'Your Google Workspace admin may block outside apps. Download it and drop it here.',
    isAvailable: () => hasEveryGoogleDriveKey(keys) && isLoaded(),
    pick: () => pickWithToken(configuredKeys(keys)),
    endImport: () => {
      if (accessToken) revokeDriveToken(accessToken)
      accessToken = null
    },
  }
}
